import { Component, input, forwardRef, signal, AfterViewInit, ElementRef, ViewChild, HostListener, OnDestroy, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';

@Component({
  selector: 'app-date',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateComponent),
      multi: true
    }
  ],
  templateUrl: './date.component.html',
  styles: []
})
export class DateComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private document = inject(DOCUMENT);
  label = input<string>('');
  placeholder = input<string>('Select date');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  control = input<AbstractControl | null | undefined>(undefined);
  minDate = input<string | undefined>(undefined);
  maxDate = input<string | undefined>(undefined);
  showCalendarIcon = input<boolean>(true);

  @ViewChild('dateInput', { static: false }) dateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('calendarPopup', { static: false }) calendarPopup!: ElementRef<HTMLDivElement>;
  
  isInModal = signal(false);

  value = signal<string>('');
  touched = signal(false);
  showCalendar = signal(false);
  currentMonth = signal<number>(new Date().getMonth());
  currentYear = signal<number>(new Date().getFullYear());
  selectedDate = signal<Date | null>(null);

  private onChange = (value: string | Date | null) => {};
  private onTouched = () => {};

  ngAfterViewInit(): void {
    // Initialize selected date from value
    // Use setTimeout to ensure writeValue has been called first
    setTimeout(() => {
      this.initializeFromValue();
      this.checkIfInModal();
    }, 0);
  }

  private checkIfInModal(): void {
    const inputElement = this.dateInput?.nativeElement;
    if (inputElement) {
      // Check if the component is inside a modal/dialog
      const modal = inputElement.closest('[class*="fixed"], [class*="modal"], [class*="dialog"]');
      this.isInModal.set(!!modal);
    }
  }

  private initializeFromValue(): void {
    const valueStr = this.value();
    if (valueStr) {
      this.parseAndSetDate(valueStr);
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.showCalendar()) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) {
      this.showCalendar.set(false);
      return;
    }

    const calendarElement = this.calendarPopup?.nativeElement;
    const inputElement = this.dateInput?.nativeElement;

    // If elements don't exist, close calendar
    if (!calendarElement || !inputElement) {
      this.showCalendar.set(false);
      return;
    }

    // Simple check: is the click target inside the calendar or input?
    const isInsideCalendar = calendarElement.contains(target);
    const isInsideInput = inputElement.contains(target);
    
    // Also check if it's the calendar icon button
    const isCalendarIcon = target.closest('button[aria-label="Open calendar"]') !== null;
    
    // If click is inside any of these, don't close
    if (isInsideCalendar || isInsideInput || isCalendarIcon) {
      return;
    }

    // For select dropdowns, check if the click is on an option that belongs to our calendar
    if (target.tagName === 'OPTION') {
      const select = target.closest('select');
      if (select && calendarElement.contains(select)) {
        return;
      }
    }

    // If we get here, click is outside - close the calendar
    this.showCalendar.set(false);
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.value.set(newValue);
    if (newValue) {
      const date = new Date(newValue + 'T00:00:00');
      this.selectedDate.set(date);
      this.currentMonth.set(date.getMonth());
      this.currentYear.set(date.getFullYear());
    }
    const dateValue = newValue ? new Date(newValue) : null;
    this.onChange(dateValue as any);
    this.markAsTouched();
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  onFocus(): void {
    if (!this.disabled()) {
      // Use setTimeout to ensure the calendar is rendered before checking modal
      setTimeout(() => {
        this.updateCalendarView();
        this.checkIfInModal();
        this.showCalendar.set(true);
      }, 0);
    }
  }

  toggleCalendar(): void {
    if (!this.disabled()) {
      const willShow = !this.showCalendar();
      this.showCalendar.set(willShow);
      if (willShow) {
        // Use setTimeout to ensure the calendar is rendered before checking modal
        setTimeout(() => {
          this.updateCalendarView();
          this.checkIfInModal();
        }, 0);
      }
    }
  }

  getCalendarPosition(): string {
    const inputElement = this.dateInput?.nativeElement;
    if (!inputElement) {
      return 'top: 100%; left: 0;';
    }

    if (this.isInModal()) {
      // Use fixed positioning when in modal
      const rect = inputElement.getBoundingClientRect();
      const calendarHeight = 400; // Approximate calendar height
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top: number;
      if (spaceBelow >= calendarHeight || spaceBelow > spaceAbove) {
        // Position below input
        top = rect.bottom + 8;
      } else {
        // Position above input
        top = rect.top - calendarHeight - 8;
      }
      
      // Ensure calendar doesn't go off screen
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - 320 - 8));
      const maxTop = window.innerHeight - calendarHeight - 8;
      const finalTop = Math.min(top, maxTop);
      
      return `top: ${finalTop}px; left: ${left}px;`;
    } else {
      // Use absolute positioning when not in modal
      return 'top: 100%; left: 0;';
    }
  }

  private updateCalendarView(): void {
    const valueStr = this.value();
    
    // First, ensure selectedDate is synced with value
    if (valueStr && !this.selectedDate()) {
      this.parseAndSetDate(valueStr);
    }
    
    // If there's a selected date, use its year and month
    if (this.selectedDate()) {
      const selected = this.selectedDate()!;
      this.currentMonth.set(selected.getMonth());
      this.currentYear.set(selected.getFullYear());
    } 
    // If there's a value but no selectedDate, parse it
    else if (valueStr) {
      this.parseAndSetDate(valueStr);
    }
    // Otherwise use current date
    else {
      this.setToCurrentDate();
    }
  }

  private parseAndSetDate(dateString: string): void {
    try {
      // Parse YYYY-MM-DD format manually to avoid timezone issues
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const monthIndex = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[2], 10);
        
        if (!isNaN(year) && !isNaN(monthIndex) && !isNaN(day) && 
            year >= 1900 && year <= 2100 &&
            monthIndex >= 0 && monthIndex <= 11 &&
            day >= 1 && day <= 31) {
          const date = new Date(year, monthIndex, day);
          if (!isNaN(date.getTime())) {
            this.selectedDate.set(date);
            // Explicitly set month and year - these control the dropdowns
            this.currentMonth.set(monthIndex);
            this.currentYear.set(year);
            return;
          }
        }
      }
    } catch {
      // Ignore parsing errors
    }
    // If parsing fails, fallback to current date
    this.setToCurrentDate();
  }

  private setToCurrentDate(): void {
    const today = new Date();
    this.currentMonth.set(today.getMonth());
    this.currentYear.set(today.getFullYear());
  }

  selectDate(day: number): void {
    const date = new Date(this.currentYear(), this.currentMonth(), day);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayStr = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${dayStr}`;
    
    this.value.set(dateString);
    this.selectedDate.set(date);
    // Ensure month/year are synced
    this.currentMonth.set(date.getMonth());
    this.currentYear.set(date.getFullYear());
    this.onChange(date);
    this.showCalendar.set(false);
    this.markAsTouched();
  }

  selectToday(): void {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    this.value.set(dateString);
    this.selectedDate.set(today);
    this.currentMonth.set(today.getMonth());
    this.currentYear.set(today.getFullYear());
    this.onChange(today);
    this.showCalendar.set(false);
    this.markAsTouched();
  }

  clearDate(): void {
    this.value.set('');
    this.selectedDate.set(null);
    this.onChange(null);
    this.markAsTouched();
  }

  previousMonth(): void {
    if (this.currentMonth() === 0) {
      this.currentMonth.set(11);
      this.currentYear.update(y => y - 1);
    } else {
      this.currentMonth.update(m => m - 1);
    }
  }

  nextMonth(): void {
    if (this.currentMonth() === 11) {
      this.currentMonth.set(0);
      this.currentYear.update(y => y + 1);
    } else {
      this.currentMonth.update(m => m + 1);
    }
  }

  previousYear(): void {
    this.currentYear.update(y => y - 1);
  }

  nextYear(): void {
    this.currentYear.update(y => y + 1);
  }

  getDaysInMonth(): number {
    return new Date(this.currentYear(), this.currentMonth() + 1, 0).getDate();
  }

  getFirstDayOfMonth(): number {
    return new Date(this.currentYear(), this.currentMonth(), 1).getDay();
  }

  getCalendarDays(): (number | null)[] {
    const days: (number | null)[] = [];
    const firstDay = this.getFirstDayOfMonth();
    const daysInMonth = this.getDaysInMonth();
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  }

  isToday(day: number): boolean {
    const today = new Date();
    return day === today.getDate() &&
           this.currentMonth() === today.getMonth() &&
           this.currentYear() === today.getFullYear();
  }

  isSelected(day: number): boolean {
    if (!this.selectedDate()) return false;
    return day === this.selectedDate()!.getDate() &&
           this.currentMonth() === this.selectedDate()!.getMonth() &&
           this.currentYear() === this.selectedDate()!.getFullYear();
  }

  isDisabled(day: number): boolean {
    const date = new Date(this.currentYear(), this.currentMonth(), day);
    const minDate = this.minDate();
    const maxDate = this.maxDate();
    if (minDate) {
      const min = new Date(minDate);
      if (date < min) return true;
    }
    if (maxDate) {
      const max = new Date(maxDate);
      if (date > max) return true;
    }
    return false;
  }

  getMonthName(): string {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return months[this.currentMonth()];
  }

  getMonthNames(): string[] {
    return ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
  }

  getYears(): number[] {
    const years: number[] = [];
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 100; // 100 years back
    const endYear = currentYear + 10; // 10 years forward
    
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  }

  onMonthChange(monthIndex: number | string): void {
    const month = typeof monthIndex === 'string' ? parseInt(monthIndex, 10) : monthIndex;
    if (!isNaN(month) && month >= 0 && month <= 11) {
      this.currentMonth.set(month);
    }
  }

  onYearChange(year: number | string): void {
    const yearNum = typeof year === 'string' ? parseInt(year, 10) : year;
    if (!isNaN(yearNum) && yearNum >= 1900 && yearNum <= 2100) {
      this.currentYear.set(yearNum);
    }
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | Date | null | undefined): void {
    if (value) {
      let dateString = '';
      let year: number | null = null;
      let month: number | null = null;
      let day: number | null = null;
      
      if (value instanceof Date) {
        // Ensure we create a new date object to avoid timezone issues
        year = value.getFullYear();
        month = value.getMonth();
        day = value.getDate();
        dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      } else if (typeof value === 'string' && value.trim() !== '') {
        dateString = value.trim();
        try {
          // Parse the date string (format: YYYY-MM-DD)
          const parts = dateString.split('-');
          if (parts.length === 3) {
            year = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            day = parseInt(parts[2], 10);
            
            // Validate the parsed values
            if (isNaN(year) || isNaN(month) || isNaN(day) ||
                year < 1900 || year > 2100 ||
                month < 0 || month > 11 ||
                day < 1 || day > 31) {
              year = null;
              month = null;
              day = null;
            }
          }
        } catch {
          // If parsing fails, values remain null
        }
      }
      
      // Set the value first
      this.value.set(dateString);
      
      // If we have valid year, month, day, set the calendar view
      if (year !== null && month !== null && day !== null) {
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          this.selectedDate.set(date);
          // Explicitly set month and year - these are what the dropdowns use
          this.currentMonth.set(month);
          this.currentYear.set(year);
        }
      }
    } else {
      this.value.set('');
      this.selectedDate.set(null);
      // Don't reset month/year when clearing - keep current view
    }
  }

  registerOnChange(fn: (value: string | Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Note: Cannot directly set signal input, but this is called by Angular forms
    // The disabled state is managed through the input signal
  }

  get hasError(): boolean {
    const control = this.control();
    const errorMsg = this.errorMessage();
    if (control) {
      return (control.invalid && (control.touched || this.touched())) || !!errorMsg;
    }
    return this.touched() && !!errorMsg;
  }

  get displayError(): string {
    const errorMsg = this.errorMessage();
    if (errorMsg) {
      return errorMsg;
    }
    const control = this.control();
    if (control && control.errors) {
      if (control.errors['required']) {
        return `${this.label() || 'This field'} is required`;
      }
      if (control.errors['min']) {
        return `Date must be after ${control.errors['min'].min}`;
      }
      if (control.errors['max']) {
        return `Date must be before ${control.errors['max'].max}`;
      }
    }
    return '';
  }

  get inputClasses(): string {
    const classes = [
      'w-full',
      'px-4',
      'py-3',
      'pr-12',
      'border',
      'rounded-lg',
      'text-base',
      'bg-white',
      'text-gray-900',
      'transition-all',
      'duration-200',
      'box-border',
      'focus:outline-none',
      'cursor-pointer',
      'dark:bg-gray-800',
      'dark:text-gray-100',
      'dark:border-gray-600',
      'shadow-sm',
      'hover:shadow-md',
      'focus:shadow-lg'
    ];

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }
    
    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800', 'shadow-none', 'hover:shadow-none');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }

  formatDisplayDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString + 'T00:00:00');
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      };
      return date.toLocaleDateString('en-US', options);
    } catch {
      return dateString;
    }
  }

  getDayClasses(day: number): string {
    const classes = [
      'aspect-square',
      'flex',
      'items-center',
      'justify-center',
      'text-sm',
      'font-medium',
      'rounded-md',
      'transition-all',
      'duration-150',
      'hover:bg-gray-100',
      'dark:hover:bg-gray-700',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:ring-offset-1'
    ];

    if (this.isDisabled(day)) {
      classes.push('opacity-40', 'cursor-not-allowed', 'hover:bg-transparent');
    } else {
      classes.push('cursor-pointer');
    }

    if (this.isToday(day)) {
      classes.push('bg-blue-50', 'dark:bg-blue-900/20', 'text-blue-600', 'dark:text-blue-400', 'font-semibold');
    } else {
      classes.push('text-gray-700', 'dark:text-gray-300');
    }

    if (this.isSelected(day)) {
      classes.push('bg-blue-600', 'dark:bg-blue-500', 'text-white', 'font-semibold', 'shadow-md');
      if (this.isToday(day)) {
        classes.push('ring-2', 'ring-blue-300', 'dark:ring-blue-700');
      }
    }

    return classes.join(' ');
  }
}
